<template>
  <div class="landing">
    <header class="landing__nav">
      <div class="landing__nav-inner">
        <div class="landing__brand">
          <span class="landing__brand-mark">S</span>
          <strong>Sauda</strong>
        </div>
        <nav class="landing__nav-links">
          <a href="#modes" class="landing__nav-link" @click="goToModes">{{ $t('public.landing.navModes') }}</a>
          <a href="#pricing" class="landing__nav-link" @click="goToPricing">{{ $t('public.landing.pricing.nav') }}</a>
          <router-link to="/blog" class="landing__nav-link">{{ $t('public.common.blog') }}</router-link>
        </nav>

        <div class="landing__nav-actions">
          <LanguageSwitcher />
          <button v-if="hasToken" type="button" class="landing__btn landing__btn--solid" @click="$router.push('/cash')">
            {{ $t('public.landing.openCash') }}
          </button>
          <template v-else>
            <button type="button" class="landing__btn landing__btn--ghost" @click="$router.push('/kassa-login')">
              {{ $t('public.common.login') }}
            </button>
            <button type="button" class="landing__btn landing__btn--solid" @click="$router.push('/register')">
              {{ $t('public.common.start') }}
            </button>
          </template>
        </div>
      </div>
    </header>

    <section class="landing__hero">
      <div class="landing__hero-blob landing__hero-blob--1"></div>
      <div class="landing__hero-blob landing__hero-blob--2"></div>
      <div class="landing__hero-inner">
        <div class="landing__hero-text">
          <span class="landing__chip landing__enter" style="--d: 0s">{{ $t('public.landing.chip') }}</span>
          <h1 class="landing__enter" style="--d: 0.08s">{{ $t('public.landing.heroTitle') }}</h1>
          <p class="landing__enter" style="--d: 0.16s">{{ $t('public.landing.heroText') }}</p>
          <div class="landing__hero-cta landing__enter" style="--d: 0.24s">
            <button type="button" class="landing__btn landing__btn--big" @click="$router.push('/register')">
              {{ $t('public.landing.register') }}
              <i class="mdi mdi-arrow-right"></i>
            </button>
            <button type="button" class="landing__btn landing__btn--big landing__btn--outline" @click="$router.push('/kassa-login')">
              {{ $t('public.landing.hasAccount') }}
            </button>
          </div>
          <div class="landing__hero-points landing__enter" style="--d: 0.32s">
            <span><i class="mdi mdi-check-circle"></i> {{ $t('public.landing.browser') }}</span>
            <span><i class="mdi mdi-check-circle"></i> {{ $t('public.landing.devices') }}</span>
            <span><i class="mdi mdi-check-circle"></i> {{ $t('public.landing.fiveMinutes') }}</span>
          </div>
        </div>

        <div class="landing__hero-visual landing__enter-visual">
          <div class="landing__mock">
            <div class="landing__mock-head">
              <span>{{ $t('public.landing.receipt') }}</span>
              <em>{{ $t('public.landing.items') }}</em>
            </div>
            <div class="landing__mock-row">
              <div>
                <strong>{{ $t('public.landing.milk') }}</strong>
                <small>350 {{ $t('public.landing.perUnit') }}</small>
              </div>
              <b>700 ₸</b>
            </div>
            <div class="landing__mock-row">
              <div>
                <strong>{{ $t('public.landing.cheese') }}</strong>
                <small>
                  <s>2000</s> 1900 {{ $t('public.landing.perKg') }}
                  <i class="landing__mock-sale">−100 ₸</i>
                </small>
              </div>
              <b>2 850 ₸</b>
            </div>
            <div class="landing__mock-row">
              <div>
                <strong>{{ $t('public.landing.bread') }}</strong>
                <small>180 {{ $t('public.landing.perUnit') }}</small>
              </div>
              <b>180 ₸</b>
            </div>
            <div class="landing__mock-total">
              <span>{{ $t('public.landing.due') }}</span>
              <strong>3 730 ₸</strong>
            </div>
            <div class="landing__mock-pay">{{ $t('public.landing.pay') }}</div>
          </div>

          <div class="landing__float landing__float--revenue">
            <small>{{ $t('public.landing.revenueToday') }}</small>
            <strong>184 500 ₸</strong>
            <span>{{ $t('public.landing.vsYesterday') }}</span>
          </div>
          <div class="landing__float landing__float--stock">
            <i class="mdi mdi-warehouse"></i>
            <div>
              <strong>{{ $t('public.landing.stockControl') }}</strong>
              <small>{{ $t('public.landing.stockAuto') }}</small>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="features" class="landing__features">
      <div class="landing__section-head reveal">
        <h2>{{ $t('public.landing.featuresTitle') }}</h2>
        <p>{{ $t('public.landing.featuresSubtitle') }}</p>
      </div>

      <div class="landing__features-grid">
        <article
          v-for="(feature, index) in features"
          :key="feature.title"
          class="landing__feature reveal"
          :style="{ '--d': `${(index % 4) * 0.08}s` }"
        >
          <div class="landing__feature-icon">
            <i :class="['mdi', feature.icon]"></i>
          </div>
          <h3>{{ feature.title }}</h3>
          <p>{{ feature.text }}</p>
        </article>
      </div>
    </section>

    <section id="modes" class="landing__modes">
      <div class="landing__section-head reveal">
        <h2>{{ $t('public.landing.modesTitle') }}</h2>
        <p>{{ $t('public.landing.modesSubtitle') }}</p>
      </div>

      <div class="landing__modes-grid">
        <article class="landing__mode reveal" style="--d: 0s">
          <div class="landing__mode-top">
            <div class="landing__mode-icon">
              <i class="mdi mdi-package-variant-closed"></i>
            </div>
            <span class="landing__mode-tag">{{ $t('public.landing.withStock') }}</span>
          </div>
          <h3>{{ $t('public.landing.productMode') }}</h3>
          <p>{{ $t('public.landing.productModeText') }}</p>
          <ul class="landing__mode-list">
            <li v-for="point in productPoints" :key="point"><i class="mdi mdi-check"></i> {{ point }}</li>
          </ul>
        </article>

        <article class="landing__mode reveal" style="--d: 0.12s">
          <div class="landing__mode-top">
            <div class="landing__mode-icon landing__mode-icon--quick">
              <i class="mdi mdi-flash"></i>
            </div>
            <span class="landing__mode-tag landing__mode-tag--quick">{{ $t('public.landing.withoutStock') }}</span>
          </div>
          <h3>{{ $t('public.landing.quickSale') }}</h3>
          <p>{{ $t('public.landing.quickSaleText') }}</p>
          <ul class="landing__mode-list">
            <li v-for="point in quickPoints" :key="point"><i class="mdi mdi-check"></i> {{ point }}</li>
          </ul>
        </article>
      </div>

      <p class="landing__modes-note reveal">
        <i class="mdi mdi-information-outline"></i>
        {{ $t('public.landing.modesNote') }}
      </p>
    </section>

    <section class="landing__audience">
      <div class="landing__section-head reveal">
        <h2>{{ $t('public.landing.audienceTitle') }}</h2>
        <p>{{ $t('public.landing.audienceSubtitle') }}</p>
      </div>
      <div class="landing__audience-row reveal">
        <div v-for="(audience, index) in audience" :key="audience" class="landing__audience-item">
          <i :class="['mdi', audienceIcons[index]]"></i>
          <span>{{ audience }}</span>
        </div>
      </div>
    </section>

    <section class="landing__steps">
      <div class="landing__section-head reveal">
        <h2>{{ $t('public.landing.stepsTitle') }}</h2>
        <p>{{ $t('public.landing.stepsSubtitle') }}</p>
      </div>
      <div class="landing__steps-grid">
        <article v-for="(step, index) in steps" :key="step.title" class="landing__step reveal" :style="{ '--d': `${index * 0.12}s` }">
          <span>{{ index + 1 }}</span>
          <h3>{{ step.title }}</h3>
          <p>{{ step.text }}</p>
        </article>
      </div>
    </section>

    <section id="pricing" class="landing__pricing">
      <div class="landing__section-head reveal">
        <h2>{{ $t('public.landing.pricing.title') }}</h2>
        <p class="landing__pricing-perks">{{ $t('public.landing.pricing.perks') }}</p>
      </div>

      <div class="landing__pricing-toggles reveal">
        <div class="landing__pricing-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            class="landing__pricing-tab"
            :class="{ 'landing__pricing-tab--active': pricingAudience === 'cafe' }"
            :aria-selected="pricingAudience === 'cafe'"
            @click="pricingAudience = 'cafe'"
          >
            {{ $t('public.landing.pricing.audienceCafe') }}
          </button>
          <button
            type="button"
            role="tab"
            class="landing__pricing-tab"
            :class="{ 'landing__pricing-tab--active': pricingAudience === 'shop' }"
            :aria-selected="pricingAudience === 'shop'"
            @click="pricingAudience = 'shop'"
          >
            {{ $t('public.landing.pricing.audienceShop') }}
          </button>
        </div>
        <span class="landing__pricing-yearly">{{ $t('public.landing.pricing.yearly') }}</span>
      </div>

      <div class="landing__pricing-grid">
        <article
          v-for="(plan, index) in pricingPlans"
          :key="plan.key"
          class="landing__price-card reveal"
          :class="{ 'landing__price-card--popular': plan.popular }"
          :style="{ '--d': `${index * 0.08}s` }"
        >
          <div v-if="plan.popular" class="landing__price-badge">
            <span class="landing__price-badge-dot"></span>
            {{ $t('public.landing.pricing.popular') }}
          </div>
          <h3>{{ plan.name }}</h3>
          <p class="landing__price-desc">{{ plan.desc }}</p>
          <div class="landing__price-amount">
            <strong>{{ plan.price }}</strong>
            <span>{{ $t('public.landing.pricing.perMonth') }}</span>
          </div>
          <button
            type="button"
            class="landing__btn landing__btn--solid landing__price-cta"
            :class="{ 'landing__price-cta--accent': plan.popular }"
            @click="$router.push('/register')"
          >
            {{ $t('public.landing.pricing.cta') }}
          </button>
          <p v-if="plan.from" class="landing__price-from">
            {{ $t('public.landing.pricing.allFrom', { plan: plan.from }) }}
          </p>
          <ul class="landing__price-list">
            <li v-for="feature in plan.features" :key="feature">
              <i class="mdi mdi-check"></i>
              <span>{{ feature }}</span>
            </li>
          </ul>
          <a href="#features" class="landing__price-all" @click="goToFeatures">
            {{ $t('public.landing.pricing.viewAll') }}
            <i class="mdi mdi-arrow-right"></i>
          </a>
        </article>
      </div>
    </section>

    <section class="landing__blog">
      <div class="landing__section-head reveal">
        <h2>{{ $t('public.landing.blogTitle') }}</h2>
        <p>{{ $t('public.landing.blogSubtitle') }}</p>
      </div>

      <div class="landing__blog-grid">
        <router-link
          v-for="(post, index) in blogPosts"
          :key="post.slug"
          :to="`/blog/${post.slug}`"
          class="landing__blog-card reveal"
          :style="{ '--d': `${(index % 4) * 0.08}s` }"
        >
          <span class="landing__blog-category">{{ post.category }}</span>
          <h3>{{ post.title }}</h3>
          <p>{{ post.excerpt }}</p>
          <div class="landing__blog-meta">
            <span><i class="mdi mdi-clock-outline"></i> {{ post.readMinutes }} {{ $t('public.common.minutes') }}</span>
            <span class="landing__blog-arrow">{{ $t('public.common.read') }} <i class="mdi mdi-arrow-right"></i></span>
          </div>
        </router-link>
      </div>

      <div class="landing__blog-cta reveal">
        <router-link to="/blog" class="landing__btn landing__btn--outline">
          {{ $t('public.common.allArticles') }}
          <i class="mdi mdi-arrow-right"></i>
        </router-link>
      </div>
    </section>

    <section class="landing__cta">
      <div class="landing__cta-inner reveal">
        <h2>{{ $t('public.landing.tryTitle') }}</h2>
        <p>{{ $t('public.landing.tryText') }}</p>
        <button type="button" class="landing__btn landing__btn--big landing__btn--light" @click="$router.push('/register')">
          {{ $t('public.common.startFree') }}
          <i class="mdi mdi-arrow-right"></i>
        </button>
      </div>
    </section>

    <footer class="landing__footer">
      <div class="landing__brand">
        <span class="landing__brand-mark">S</span>
        <strong>Sauda</strong>
      </div>
      <nav class="landing__footer-links">
        <a href="#modes" @click="goToModes">{{ $t('public.landing.navModes') }}</a>
        <a href="#pricing" @click="goToPricing">{{ $t('public.landing.pricing.nav') }}</a>
        <router-link to="/blog">{{ $t('public.common.blog') }}</router-link>
        <router-link to="/kassa-login">{{ $t('public.common.login') }}</router-link>
        <router-link to="/register">{{ $t('public.common.startFree') }}</router-link>
      </nav>
      <p>{{ $t('public.common.footer', { year: new Date().getFullYear() }) }}</p>
    </footer>
  </div>
</template>

<script>
import LanguageSwitcher from '@/components/LanguageSwitcher'
import { getBlogPosts } from '@/data/blogPosts'

export default {
  name: 'LandingPage',
  components: { LanguageSwitcher },
  data() {
    return {
      pricingAudience: 'cafe',
    }
  },
  computed: {
    blogPosts() {
      return getBlogPosts(this.$i18n.locale)
    },
    features() {
      return [
        ['mdi-point-of-sale', 'cash'], ['mdi-warehouse', 'stock'], ['mdi-sale-outline', 'discounts'], ['mdi-account-group-outline', 'cashiers'],
        ['mdi-chart-line', 'analytics'], ['mdi-hand-coin-outline', 'debts'], ['mdi-scale', 'weighted'], ['mdi-cellphone-link', 'devices'],
      ].map(([icon, key]) => ({ icon, title: this.$t(`public.landing.features.${key}.title`), text: this.$t(`public.landing.features.${key}.text`) }))
    },
    productPoints() {
      return this.$t('public.landing.productPoints')
    },
    quickPoints() {
      return this.$t('public.landing.quickPoints')
    },
    audience() {
      return this.$t('public.landing.audience')
    },
    audienceIcons() {
      return ['mdi-store-outline', 'mdi-coffee-outline', 'mdi-food-outline', 'mdi-home-city-outline']
    },
    steps() {
      return this.$t('public.landing.steps')
    },
    pricingPlans() {
      const keys = ['starter', 'mini', 'business', 'pro']
      const fromMap = { mini: 'Starter', business: 'Mini', pro: 'Business' }
      return keys.map((key) => ({
        key,
        name: this.$t(`public.landing.pricing.plans.${key}.name`),
        desc: this.$t(`public.landing.pricing.plans.${key}.desc`),
        price: this.$t(`public.landing.pricing.plans.${key}.price`),
        features: this.$t(`public.landing.pricing.plans.${key}.features`),
        popular: key === 'business',
        from: fromMap[key] || null,
      }))
    },
    hasToken() {
      return !!localStorage.getItem('access_token')
    },
  },
  methods: {
    scrollToId(id) {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    goToModes(e) {
      if (e) e.preventDefault()
      this.scrollToId('modes')
    },
    goToPricing(e) {
      if (e) e.preventDefault()
      this.scrollToId('pricing')
    },
    goToFeatures(e) {
      if (e) e.preventDefault()
      this.scrollToId('features')
    },
  },
  mounted() {
    if (this.$route.hash === '#modes') {
      this.$nextTick(() => setTimeout(() => this.goToModes(), 60))
    }
    if (this.$route.hash === '#pricing') {
      this.$nextTick(() => setTimeout(() => this.goToPricing(), 60))
    }
    if (this.$route.hash === '#features') {
      this.$nextTick(() => setTimeout(() => this.goToFeatures(), 60))
    }

    // Reveal-анимации при прокрутке
    const show = (el) => {
      const delay = parseFloat(el.style.getPropertyValue('--d')) || 0
      setTimeout(() => {
        el.classList.add('reveal--in')
      }, delay * 1000)
    }

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              show(entry.target)
              this.observer.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.15 }
      )
      this.$el.querySelectorAll('.reveal').forEach((el) => this.observer.observe(el))
    } else {
      this.$el.querySelectorAll('.reveal').forEach((el) => el.classList.add('reveal--in'))
    }
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
  },
}
</script>

<style scoped lang="scss">
$ink: #152033;
$muted: #6b7a90;
$accent: #0b4f8a;
$dark: #10243a;

.landing {
  min-height: 100vh;
  background: #f6f9fc;
  color: $ink;
  font-size: 16px;
  overflow-x: hidden;
}

/* ---------- Анимации ---------- */
@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(22px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInMock {
  from {
    opacity: 0;
    transform: translateX(40px) rotate(2deg);
  }
  to {
    opacity: 1;
    transform: translateX(0) rotate(0);
  }
}

@keyframes floatY {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes floatYSoft {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(7px);
  }
}

@keyframes blobDrift {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -24px) scale(1.08);
  }
  66% {
    transform: translate(-22px, 18px) scale(0.95);
  }
}

@keyframes payPulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.45);
  }
  55% {
    box-shadow: 0 0 0 12px rgba(245, 158, 11, 0);
  }
}

@keyframes markShine {
  0%,
  60%,
  100% {
    transform: translateX(-120%) skewX(-18deg);
  }
  80% {
    transform: translateX(220%) skewX(-18deg);
  }
}

/* Появление hero при загрузке */
.landing__enter {
  opacity: 0;
  animation: fadeUp 0.7s cubic-bezier(0.22, 0.8, 0.3, 1) forwards;
  animation-delay: var(--d, 0s);
}

.landing__enter-visual {
  opacity: 0;
  animation: slideInMock 0.9s cubic-bezier(0.22, 0.8, 0.3, 1) 0.25s forwards;
}

/* Появление секций при скролле (задержка --d обрабатывается в JS) */
.reveal {
  opacity: 0;
  transform: translateY(28px);
  transition:
    opacity 0.65s cubic-bezier(0.22, 0.8, 0.3, 1),
    transform 0.65s cubic-bezier(0.22, 0.8, 0.3, 1);

  &--in {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .landing__enter,
  .landing__enter-visual,
  .reveal {
    opacity: 1 !important;
    transform: none !important;
    animation: none !important;
    transition: none !important;
  }

  .landing__float,
  .landing__mock-pay,
  .landing__hero-blob {
    animation: none !important;
  }
}

/* ---------- Топбар ---------- */
.landing__nav {
  position: sticky;
  top: 0;
  z-index: 30;
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid #e3ebf3;
}

.landing__nav-inner {
  max-width: 1160px;
  margin: 0 auto;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.landing__brand {
  display: flex;
  align-items: center;
  gap: 10px;

  strong {
    font-size: 19px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }
}

.landing__brand-mark {
  position: relative;
  overflow: hidden;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, #163a52, $accent);
  color: #fff;
  font-weight: 800;
  font-size: 18px;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 55%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.45), transparent);
    animation: markShine 4.5s ease-in-out infinite;
  }
}

.landing__nav-links {
  display: flex;
  align-items: center;
  gap: 22px;
}

.landing__nav-link {
  font-size: 14px;
  font-weight: 650;
  color: $ink;
  text-decoration: none;
  transition: color 0.18s ease;

  &:hover {
    color: $accent;
  }
}

.landing__nav-actions {
  display: flex;
  gap: 10px;
}

/* ---------- Кнопки ---------- */
.landing__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border: 0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;

  &--solid {
    background: $accent;
    color: #fff;

    &:hover {
      background: #093f6d;
    }
  }

  &--ghost {
    background: transparent;
    color: $ink;

    &:hover {
      background: #e9f1f8;
    }
  }

  &--outline {
    background: #fff;
    color: $accent;
    border: 1.5px solid #c4d6e6;

    &:hover {
      border-color: $accent;
    }
  }

  &--big {
    padding: 15px 26px;
    font-size: 16px;
    border-radius: 14px;
    background: $accent;
    color: #fff;

    &:hover {
      background: #093f6d;
      transform: translateY(-2px);
      box-shadow: 0 12px 26px rgba(11, 79, 138, 0.35);
    }

    .mdi {
      font-size: 18px;
      transition: transform 0.18s ease;
    }

    &:hover .mdi {
      transform: translateX(4px);
    }
  }

  &--big.landing__btn--outline {
    background: #fff;
    color: $accent;

    &:hover {
      background: #f2f7fb;
    }
  }

  &--light {
    background: #fff;
    color: $accent;

    &:hover {
      background: #eef4fa;
      transform: translateY(-1px);
    }
  }
}

/* ---------- Hero ---------- */
.landing__hero {
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at 85% 10%, rgba(90, 168, 220, 0.22), transparent 40%),
    linear-gradient(160deg, #143252 0%, $dark 60%, #0d1c2e 100%);
  color: #fff;
}

.landing__hero-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  pointer-events: none;
  animation: blobDrift 14s ease-in-out infinite;

  &--1 {
    width: 420px;
    height: 420px;
    top: -140px;
    left: -120px;
    background: rgba(64, 145, 200, 0.28);
  }

  &--2 {
    width: 360px;
    height: 360px;
    bottom: -160px;
    right: 8%;
    background: rgba(245, 158, 11, 0.14);
    animation-delay: -7s;
    animation-duration: 18s;
  }
}

.landing__hero-inner {
  position: relative;
  z-index: 1;
  max-width: 1160px;
  margin: 0 auto;
  padding: 72px 20px 88px;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 48px;
  align-items: center;
}

.landing__chip {
  display: inline-block;
  padding: 7px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.16);
  font-size: 13px;
  font-weight: 650;
  margin-bottom: 20px;
}

.landing__hero-text {
  h1 {
    margin: 0 0 18px;
    font-size: 44px;
    font-weight: 800;
    line-height: 1.15;
    letter-spacing: -0.025em;
  }

  > p {
    margin: 0 0 28px;
    font-size: 17px;
    line-height: 1.65;
    opacity: 0.82;
    max-width: 520px;
  }
}

.landing__hero-cta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 26px;
}

.landing__hero-points {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 22px;
  font-size: 13.5px;
  opacity: 0.9;

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .mdi {
    color: #34d399;
    font-size: 17px;
  }
}

/* ---------- Мок кассы ---------- */
.landing__hero-visual {
  position: relative;
  display: flex;
  justify-content: center;
}

.landing__mock {
  width: 100%;
  max-width: 360px;
  padding: 18px;
  border-radius: 22px;
  background: #fff;
  color: $ink;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
}

.landing__mock-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;

  span {
    padding: 7px 14px;
    border-radius: 10px;
    background: $accent;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
  }

  em {
    font-style: normal;
    font-size: 12px;
    color: $muted;
    font-weight: 650;
  }
}

.landing__mock-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 11px 12px;
  border: 1px solid #e3ebf3;
  border-radius: 12px;
  margin-bottom: 8px;

  strong {
    display: block;
    font-size: 14px;
    font-weight: 700;
  }

  small {
    color: $muted;
    font-size: 12px;

    s {
      opacity: 0.7;
    }
  }

  b {
    font-size: 14px;
    color: $accent;
    white-space: nowrap;
  }
}

.landing__mock-sale {
  font-style: normal;
  margin-left: 5px;
  padding: 1px 6px;
  border-radius: 6px;
  background: #e7f6ec;
  color: #1e7d3e;
  font-size: 11px;
  font-weight: 700;
}

.landing__mock-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 12px 0;
  padding: 14px 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #12324f, $accent);
  color: #fff;

  span {
    font-size: 13px;
    opacity: 0.85;
  }

  strong {
    font-size: 19px;
    font-weight: 780;
  }
}

.landing__mock-pay {
  padding: 14px;
  border-radius: 12px;
  background: linear-gradient(180deg, #f59e0b, #d97706);
  color: #fff;
  text-align: center;
  font-weight: 780;
  font-size: 15px;
  animation: payPulse 2.6s ease-out infinite;
}

.landing__float {
  position: absolute;
  border-radius: 16px;
  background: #fff;
  color: $ink;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.28);
  padding: 14px 16px;
  animation: floatY 5s ease-in-out infinite;

  &--stock {
    animation: floatYSoft 6s ease-in-out infinite;
    animation-delay: -2.5s;
  }

  &--revenue {
    top: -18px;
    right: -6px;

    small {
      display: block;
      color: $muted;
      font-size: 11.5px;
    }

    strong {
      display: block;
      font-size: 18px;
      font-weight: 800;
      margin: 2px 0;
    }

    span {
      font-size: 11.5px;
      color: #1e7d3e;
      font-weight: 700;
    }
  }

  &--stock {
    bottom: -16px;
    left: -10px;
    display: flex;
    align-items: center;
    gap: 10px;

    .mdi {
      font-size: 24px;
      color: $accent;
    }

    strong {
      display: block;
      font-size: 13px;
      font-weight: 750;
    }

    small {
      color: $muted;
      font-size: 11.5px;
    }
  }
}

/* ---------- Функции ---------- */
.landing__features {
  max-width: 1160px;
  margin: 0 auto;
  padding: 72px 20px 24px;
}

.landing__section-head {
  text-align: center;
  margin-bottom: 40px;

  h2 {
    margin: 0 0 8px;
    font-size: 32px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }

  p {
    margin: 0;
    color: $muted;
    font-size: 15.5px;
  }
}

.landing__features-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.landing__feature {
  padding: 22px;
  background: #fff;
  border: 1px solid #e3ebf3;
  border-radius: 18px;
  transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;

  &.reveal {
    transition:
      opacity 0.6s cubic-bezier(0.22, 0.8, 0.3, 1),
      transform 0.3s ease, border-color 0.22s ease, box-shadow 0.22s ease;
  }

  &:hover {
    transform: translateY(-6px);
    border-color: #bcd2e4;
    box-shadow: 0 18px 40px rgba(18, 32, 51, 0.12);
  }

  &:hover .landing__feature-icon {
    background: $accent;
    color: #fff;
    transform: scale(1.08) rotate(-4deg);
  }

  h3 {
    margin: 14px 0 8px;
    font-size: 16.5px;
    font-weight: 750;
  }

  p {
    margin: 0;
    color: $muted;
    font-size: 13.5px;
    line-height: 1.55;
  }
}

.landing__feature-icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: #e9f1f8;
  color: $accent;
  transition: background 0.22s ease, color 0.22s ease, transform 0.22s ease;

  .mdi {
    font-size: 24px;
  }
}

/* ---------- Шаги ---------- */
.landing__steps {
  max-width: 1160px;
  margin: 0 auto;
  padding: 56px 20px;
}

.landing__steps-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.landing__step {
  position: relative;
  padding: 26px 22px;
  background: #fff;
  border: 1px solid #e3ebf3;
  border-radius: 18px;

  &.reveal {
    transition:
      opacity 0.6s cubic-bezier(0.22, 0.8, 0.3, 1),
      transform 0.3s ease, border-color 0.22s ease, box-shadow 0.22s ease;
  }

  &:hover {
    transform: translateY(-5px);
    border-color: #bcd2e4;
    box-shadow: 0 16px 36px rgba(18, 32, 51, 0.1);
  }

  &:hover span {
    transform: scale(1.1);
  }

  span {
    display: inline-grid;
    place-items: center;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: linear-gradient(135deg, #163a52, $accent);
    color: #fff;
    font-size: 18px;
    font-weight: 800;
    transition: transform 0.22s ease;
  }

  h3 {
    margin: 14px 0 8px;
    font-size: 17px;
    font-weight: 750;
  }

  p {
    margin: 0;
    color: $muted;
    font-size: 14px;
    line-height: 1.55;
  }
}

/* ---------- Режимы кассы ---------- */
.landing__modes {
  max-width: 1160px;
  margin: 0 auto;
  padding: 56px 20px;
}

.landing__modes-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.landing__mode {
  padding: 28px;
  background: #fff;
  border: 1px solid #e3ebf3;
  border-radius: 20px;
  transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;

  &.reveal {
    transition:
      opacity 0.6s cubic-bezier(0.22, 0.8, 0.3, 1),
      transform 0.3s ease, border-color 0.22s ease, box-shadow 0.22s ease;
  }

  &:hover {
    transform: translateY(-6px);
    border-color: #bcd2e4;
    box-shadow: 0 18px 40px rgba(18, 32, 51, 0.12);
  }

  h3 {
    margin: 0 0 6px;
    font-size: 21px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }

  > p {
    margin: 0 0 18px;
    color: $muted;
    font-size: 14px;
  }
}

.landing__mode-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.landing__mode-icon {
  width: 52px;
  height: 52px;
  border-radius: 15px;
  display: grid;
  place-items: center;
  background: #e9f1f8;
  color: $accent;

  .mdi {
    font-size: 26px;
  }

  &--quick {
    background: #fdf1de;
    color: #d97706;
  }
}

.landing__mode-tag {
  padding: 5px 12px;
  border-radius: 999px;
  background: #e9f1f8;
  color: $accent;
  font-size: 12px;
  font-weight: 700;

  &--quick {
    background: #fdf1de;
    color: #d97706;
  }
}

.landing__mode-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 10px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    font-size: 14px;
    color: $ink;
    line-height: 1.5;
  }

  .mdi {
    color: #1e7d3e;
    font-size: 17px;
    margin-top: 2px;
    flex-shrink: 0;
  }
}

.landing__modes-note {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  margin: 26px 0 0;
  padding: 12px 18px;
  border-radius: 12px;
  background: #eef4fa;
  color: $accent;
  font-size: 13.5px;
  font-weight: 650;
  text-align: center;

  .mdi {
    font-size: 18px;
    flex-shrink: 0;
  }
}

/* ---------- Кому подходит ---------- */
.landing__audience {
  max-width: 1160px;
  margin: 0 auto;
  padding: 8px 20px 56px;
}

.landing__audience-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;
}

.landing__audience-item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 12px 20px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e3ebf3;
  font-size: 14px;
  font-weight: 650;
  color: $ink;

  .mdi {
    font-size: 19px;
    color: $accent;
  }
}

/* ---------- Блог ---------- */
.landing__blog {
  max-width: 1160px;
  margin: 0 auto;
  padding: 56px 20px 24px;
}

.landing__blog-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.landing__blog-card {
  display: flex;
  flex-direction: column;
  padding: 22px;
  background: #fff;
  border: 1px solid #e3ebf3;
  border-radius: 18px;
  text-decoration: none;
  color: $ink;
  transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;

  &.reveal {
    transition:
      opacity 0.6s cubic-bezier(0.22, 0.8, 0.3, 1),
      transform 0.3s ease, border-color 0.22s ease, box-shadow 0.22s ease;
  }

  &:hover {
    transform: translateY(-6px);
    border-color: #bcd2e4;
    box-shadow: 0 18px 40px rgba(18, 32, 51, 0.12);
  }

  &:hover .landing__blog-arrow {
    color: #093f6d;
  }

  &:hover .landing__blog-arrow .mdi {
    transform: translateX(4px);
  }

  h3 {
    margin: 12px 0 8px;
    font-size: 15.5px;
    font-weight: 750;
    line-height: 1.35;
  }

  > p {
    margin: 0;
    color: $muted;
    font-size: 13px;
    line-height: 1.55;
    flex-grow: 1;
  }
}

.landing__blog-category {
  display: inline-block;
  align-self: flex-start;
  padding: 4px 11px;
  border-radius: 999px;
  background: #e9f1f8;
  color: $accent;
  font-size: 11.5px;
  font-weight: 700;
}

.landing__blog-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #eef2f6;
  font-size: 12.5px;
  color: $muted;

  .mdi {
    font-size: 14px;
    vertical-align: -1px;
  }
}

.landing__blog-arrow {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: $accent;
  font-weight: 700;
  transition: color 0.18s ease;

  .mdi {
    font-size: 15px;
    transition: transform 0.18s ease;
  }
}

.landing__blog-cta {
  display: flex;
  justify-content: center;
  margin-top: 28px;
}

/* ---------- Тарифы ---------- */
.landing__pricing {
  max-width: 1160px;
  margin: 0 auto;
  padding: 56px 20px 24px;
}

.landing__pricing-perks {
  margin: 0;
  color: $muted;
  font-size: 15px;
  font-weight: 550;
  letter-spacing: 0.01em;
}

.landing__pricing-toggles {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 14px 20px;
  margin: 0 0 28px;
}

.landing__pricing-tabs {
  display: inline-flex;
  padding: 4px;
  background: #e8eef5;
  border-radius: 14px;
}

.landing__pricing-tab {
  border: 0;
  background: transparent;
  color: $muted;
  font: inherit;
  font-size: 13.5px;
  font-weight: 650;
  padding: 10px 16px;
  border-radius: 11px;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;

  &--active {
    background: #fff;
    color: $ink;
    box-shadow: 0 4px 14px rgba(18, 32, 51, 0.1);
  }
}

.landing__pricing-yearly {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 999px;
  background: #e7f6ec;
  color: #1b7a3d;
  font-size: 13px;
  font-weight: 700;
}

.landing__pricing-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  align-items: stretch;
}

.landing__price-card {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 24px 20px 22px;
  background: #fff;
  border: 1px solid #e3ebf3;
  border-radius: 20px;
  transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;

  &.reveal {
    transition:
      opacity 0.6s cubic-bezier(0.22, 0.8, 0.3, 1),
      transform 0.3s ease, border-color 0.22s ease, box-shadow 0.22s ease;
  }

  &:hover {
    transform: translateY(-6px);
    border-color: #bcd2e4;
    box-shadow: 0 18px 40px rgba(18, 32, 51, 0.12);
  }

  &--popular {
    border-color: $accent;
    box-shadow: 0 16px 36px rgba(11, 79, 138, 0.16);
    background:
      linear-gradient(#fff, #fff) padding-box,
      linear-gradient(160deg, #0b4f8a, #5aa8dc) border-box;
    border: 2px solid transparent;
  }

  h3 {
    margin: 0 0 8px;
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }
}

.landing__price-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  margin-bottom: 10px;
  padding: 5px 10px;
  border-radius: 999px;
  background: #e9f1f8;
  color: $accent;
  font-size: 12px;
  font-weight: 750;
}

.landing__price-badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: $accent;
  box-shadow: 0 0 0 3px rgba(11, 79, 138, 0.18);
}

.landing__price-desc {
  margin: 0 0 18px;
  color: $muted;
  font-size: 13.5px;
  line-height: 1.5;
  min-height: 3.1em;
}

.landing__price-amount {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 16px;

  strong {
    font-size: 28px;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: $ink;
  }

  span {
    color: $muted;
    font-size: 13.5px;
    font-weight: 600;
  }
}

.landing__price-cta {
  width: 100%;
  justify-content: center;
  margin-bottom: 16px;

  &--accent {
    background: $accent;
  }
}

.landing__price-from {
  margin: 0 0 10px;
  color: $muted;
  font-size: 12.5px;
  font-weight: 650;
}

.landing__price-list {
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
  display: grid;
  gap: 9px;
  flex: 1;

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: $ink;
    font-size: 13.5px;
    line-height: 1.4;
  }

  .mdi {
    margin-top: 1px;
    color: #1b7a3d;
    font-size: 16px;
    flex-shrink: 0;
  }
}

.landing__price-all {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: auto;
  color: $accent;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  transition: gap 0.18s ease;

  &:hover {
    gap: 8px;
  }
}

/* ---------- CTA ---------- */
.landing__cta {
  padding: 0 20px 72px;
}

.landing__cta-inner {
  max-width: 1160px;
  margin: 0 auto;
  padding: 52px 32px;
  border-radius: 26px;
  text-align: center;
  color: #fff;
  background:
    radial-gradient(circle at 15% 20%, rgba(90, 168, 220, 0.3), transparent 40%),
    linear-gradient(135deg, #143252, $dark);

  h2 {
    margin: 0 0 10px;
    font-size: 30px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }

  p {
    margin: 0 0 26px;
    opacity: 0.82;
    font-size: 15.5px;
  }
}

/* ---------- Футер ---------- */
.landing__footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 28px 20px 36px;
  border-top: 1px solid #e3ebf3;

  p {
    margin: 0;
    color: $muted;
    font-size: 13px;
    text-align: center;
  }
}

.landing__footer-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 18px;

  a {
    font-size: 13.5px;
    font-weight: 650;
    color: $muted;
    text-decoration: none;
    transition: color 0.18s ease;

    &:hover {
      color: $accent;
    }
  }
}

/* ---------- Адаптив ---------- */
@media (max-width: 1024px) {
  .landing__features-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .landing__blog-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .landing__pricing-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .landing__hero-inner {
    gap: 36px;
  }

  .landing__hero-text h1 {
    font-size: 36px;
  }
}

@media (max-width: 860px) {
  .landing__hero-inner {
    grid-template-columns: 1fr;
    padding: 48px 20px 72px;
  }

  .landing__hero-visual {
    margin-top: 10px;
  }

  .landing__nav-links {
    display: none;
  }

  .landing__steps-grid {
    grid-template-columns: 1fr;
  }

  .landing__modes-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .landing__nav-inner {
    padding: 10px 14px;
  }

  .landing__btn--ghost {
    display: none;
  }

  .landing__hero-text h1 {
    font-size: 29px;
  }

  .landing__hero-cta .landing__btn--big {
    width: 100%;
  }

  .landing__features-grid {
    grid-template-columns: 1fr;
  }

  .landing__pricing-grid {
    grid-template-columns: 1fr;
  }

  .landing__price-desc {
    min-height: 0;
  }

  .landing__features {
    padding-top: 52px;
  }

  .landing__section-head h2 {
    font-size: 25px;
  }

  .landing__float--revenue {
    top: -14px;
    right: 0;
  }

  .landing__float--stock {
    display: none;
  }

  .landing__mode {
    padding: 22px;
  }

  .landing__audience-row {
    gap: 10px;
  }

  .landing__audience-item {
    padding: 10px 14px;
    font-size: 13px;
  }

  .landing__blog-grid {
    grid-template-columns: 1fr;
  }

  .landing__footer-links {
    gap: 12px 16px;
  }

  .landing__cta-inner {
    padding: 38px 20px;

    h2 {
      font-size: 24px;
    }
  }
}
</style>
